import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { packKeys } from '../hooks/usePacks';
import { toast } from 'sonner';
import {
  ActivityImage,
  ActivityHeader,
  ActivityActionButtons,
  ActivityDescription,
  ActivitySteps,
  ActivityTips,
  WhyItWorksBox,
  FeedbackSection,
  RecommendationsSection,
  ActivityTopBar,
} from '../components/activity-details';
import { ShareModal } from '../components/share';
import {
  isWebShareSupported,
  shareViaWebShareAPI,
  getActivityShareUrl,
} from '../utils/shareUtils';
import { api } from '../services/api';
import { transformActivityDetails } from '../utils/activityTransformers';
import ErrorMessage from '../components/card-deck/ErrorMessage';
import type { ActivityDetailsData } from '../components/activity-details/types';
import type { ActivityDetailsResponse } from '../types/activity';
import { useTimeOfDay, HomeV2Layout } from '../components/home-v2';
import { useAuth } from '../contexts/AuthContext';
import { useGuestAuth } from '../contexts/GuestAuthContext';
import { CheckInTransitionOverlay, useCheckInTransition } from '../components/home-v2/check-in-transition';
import { useContentManager } from './HomeV2Page';

/**
 * ActivityDetailsPage
 * 
 * Main page component for the Activity Details (Spark Page) screen.
 * Combines all sections into a complete scrollable view.
 * 
 * Layout:
 * - Reuses HomeV2Header (included in ActivityDetailsLayout)
 * - NO footer navigation (this is a focused detail view)
 * - White scrollable content container with rounded top corners
 * 
 * Route: /activity/:id
 */
export default function ActivityDetailsPage() {
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [activity, setActivity] = useState<ActivityDetailsData | null>(null);
  const [backendActivity, setBackendActivity] = useState<ActivityDetailsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Time-of-day theming
  //const [overridePeriod, setOverridePeriod] = useState<TimePeriod | null>(null);
  //const { period, config } = useTimeOfDay({ overridePeriod });
  const { config } = useTimeOfDay();

    const {
      //cloudRef,
      isTransitioning,
      cloudRect,
      startTransition,
      onTransitionComplete,
      // resetTransition,
    } = useCheckInTransition();

  // Auth context for deep link auto guest login
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const { isGuestAuthActive } = useGuestAuth();
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);

  // Auto guest login for users arriving via share link (deep link)
  // This happens in parallel with activity fetch - activity loads immediately
  // while guest login ensures user can interact (bookmark, feedback, etc.)
  useEffect(() => {
    // Skip if:
    // - Already authenticated
    // - Still checking localStorage for existing auth
    // - Already in the process of auto-logging in
    // - GuestAuthContext is handling auto login (provider is in tree)
    if (isAuthenticated || authLoading || isAutoLoggingIn || isGuestAuthActive) {
      return;
    }

    console.log('[ActivityDetailsPage] User not authenticated, triggering auto guest login for share link access...');
    setIsAutoLoggingIn(true);

    api.guestLogin()
      .then(response => {
        login(response);
        console.log('[ActivityDetailsPage] Auto guest login successful:', response.user.nickname);
      })
      .catch(error => {
        console.error('[ActivityDetailsPage] Auto guest login failed:', error);
        // Show user-facing error with retry option
        toast.error('Failed to log in. Some features may be unavailable.', {
          action: {
            label: 'Retry',
            onClick: () => window.location.reload()
          },
          duration: 10000 // Show longer so user has time to read and act
        });
      })
      .finally(() => {
        setIsAutoLoggingIn(false);
      });
  }, [isAuthenticated, authLoading, isAutoLoggingIn, isGuestAuthActive, login]);

  useEffect(() => {

    const fetchActivityDetails = async () => {

      if (!id) {
        setError('Activity ID is missing');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const backendActivityData = await api.getActivityById(id);
        const transformedActivity = transformActivityDetails(backendActivityData);
        setBackendActivity(backendActivityData);
        setActivity(transformedActivity);
      } catch (err) {
        console.error('Error fetching activity details:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load activity details'
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivityDetails();
  }, [id]);

  // Track activity view after 5 seconds on page
  // Timer starts only after activity loads successfully
  // Clears on unmount (user navigates away before 5s)
  useEffect(() => {
    if (!activity || !id || !isAuthenticated) return;

    const timer = setTimeout(async () => {
      try {
        await api.viewActivity(id);
        track('view_activity', { activity_id: String(id) });
      } catch (error) {
        // Silently fail — view tracking is non-critical
        console.error('[ActivityDetailsPage] Failed to record view:', error);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [activity, id, isAuthenticated]);

  const handleBackClick = () => {
    navigate('/home');
  };

  // Handler for feedback updates
  const handleFeedbackUpdate = (statistics: { thumbsUpCount: number; thumbsDownCount: number }) => {
    if (!activity) return;
    setActivity({ ...activity, likesCount: statistics.thumbsUpCount });
    queryClient.invalidateQueries({ queryKey: packKeys.all });
  };

  const handleShare = async () => {
    if (!activity) {
      toast.error('Activity not available');
      return;
    }

    // If this is a generated activity, mark it as shared first
    if (backendActivity?.isGenerated) {
      try {
        await api.shareGeneratedActivity(activity.id);
        console.log('[ActivityDetailsPage] Generated activity marked as shared');
      } catch (error) {
        console.error('[ActivityDetailsPage] Failed to mark activity as shared:', error);
        // If sharing fails (e.g., not owner, reported), show error
        if (error instanceof Error) {
          toast.error(error.message || 'Unable to share this activity');
        } else {
          toast.error('Unable to share this activity');
        }
        return; // Don't proceed with sharing if marking failed
      }
    }

    const shareUrl = getActivityShareUrl(activity.id);
    const shareData = {
      title: activity.title,
      text: `Check out this activity: ${activity.title}`,
      url: shareUrl,
    };

    // Check if Web Share API is supported (primarily mobile)
    if (isWebShareSupported()) {
      try {
        const shared = await shareViaWebShareAPI(shareData);
        if (shared) {
          track('activity_shared', { share_platform: 'native', activity_id: String(activity.id) });
          toast.success('Activity shared successfully!');
        }
      } catch (error) {
        // If Web Share API fails, fall back to modal
        console.error('Web Share API failed:', error);
        toast.info('Opening share options...');
        setIsShareModalOpen(true);
      }
    } else {
      // Desktop or browsers without Web Share API - show modal
      setIsShareModalOpen(true);
    }
  };

  const handleViewRecommendation = (id: string) => {
    // Navigate to the recommended activity
    navigate(`/activity/${id}`);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="fixed top-0 left-0 right-0 z-10 bg-white border-b border-gray-200">
          <div className="flex items-center px-4 h-14">
            <button
              onClick={handleBackClick}
              className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Go back"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
        </div>
        <div className="pt-14 flex items-center justify-center min-h-screen">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <p className="text-gray-600">Loading activity details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !activity) {
    // Determine if this is a "not found" type error
    const isNotFoundError = error?.toLowerCase().includes('not found') ||
                            error?.toLowerCase().includes('activity id is missing');

    const errorMessage = isNotFoundError
      ? "This activity doesn't exist or hasn't been shared yet."
      : error || 'Failed to load activity details';

    return (
      <div className="min-h-screen bg-white">
        <div className="fixed top-0 left-0 right-0 z-10 bg-white border-b border-gray-200">
          <div className="flex items-center px-4 h-14">
            <button
              onClick={handleBackClick}
              className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Go back"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
        </div>
        <div className="pt-14 flex items-center justify-center min-h-screen px-4">
          <ErrorMessage
            message={errorMessage}
            onRetry={handleBackClick}
            buttonText="Go to Home"
          />
        </div>
      </div>
    );
  }

    const {
      hideHeaderAvatar
    } = useContentManager()

  console.log('activity imageurl', activity.imageUrl);

  // const topBar = useMemo(
  //   () => <ActivityTopBar recommendedFor={activity.recommendedFor} />,
  //   [activity.recommendedFor]
  // );

  // Success state - render activity details
  return (
    <>
      <HomeV2Layout
        timeOfDayConfig={config}
        contentStyle="white-container"
        showNightDecorations={false}
        showMorningGlow={false}
        showCloudLogo
        showAvatar={!hideHeaderAvatar}
        onCloudClick={startTransition}
      >
        <div
          style={{
            backgroundColor: config.headerBackground,
            transition: 'background-color 0.5s ease-in-out',
          }}>
          <ActivityTopBar />
        </div>

      {/* Activity Image */}
      <ActivityImage
        imageUrl={activity.imageUrl}
        title={activity.title}
        likesCount={activity.likesCount}
      />

      {/* Activity Header (Title, Metadata, Tags) */}
      <ActivityHeader
        title={activity.title}
        metadata={activity.metadata}
        tags={activity.tags}
      />

      {/* AI-Generated Message */}
      {backendActivity?.isGenerated && (
        <div className="px-6 sm:px-8 md:px-10 lg:px-12 pt-3">
          <div className="flex items-center gap-2 text-sm" style={{
            fontFamily: 'DM Sans, sans-serif',
            color: '#6B7280'
          }}>
            <span>🤖</span>
            <span className="font-medium">AI-generated idea.</span>
            {
              !(backendActivity?.isBookmarked ?? false) && (<span>Save it if you&apos;d like to use it later.</span>)
            }
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {activity && backendActivity && (
        <ActivityActionButtons
          activityId={parseInt(id!)}
          isBookmarked={backendActivity.isBookmarked}
          onShare={handleShare}
          onBookmarkChange={(isBookmarked) => {
            if (isBookmarked) {
              track('activity_saved', { activity_id: String(id) });
            }
            // Update local state to reflect bookmark status
            setBackendActivity((prev) =>
              prev ? { ...prev, isBookmarked } : null
            );
          }}
        />
      )}

      {/* Description */}
      <ActivityDescription description={activity.description} />

      {/* Try This Steps */}
      <ActivitySteps steps={activity.steps} />

      {/* Tips */}
      <ActivityTips tips={activity.tips} />

      {/* Why It Works */}
      <WhyItWorksBox
        text={activity.whyItWorks.text}
        boldKeywords={activity.whyItWorks.boldKeywords}
      />

      {/* Feedback */}
      <FeedbackSection
        activityId={id!}
        initialFeedback={backendActivity?.userFeedback?.feedbackType}
        onFeedbackUpdate={handleFeedbackUpdate}
      />

      {/* Recommendations */}
      <RecommendationsSection
        recommendations={activity.recommendations}
        onViewActivity={handleViewRecommendation}
      />

      {/* Share Modal */}
      {activity && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          activityId={activity.id}
          activityTitle={activity.title}
          shareUrl={getActivityShareUrl(activity.id)}
        />
      )}
      </HomeV2Layout>

       {/* Check-in transition overlay */}
            {isTransitioning && (
              <CheckInTransitionOverlay
                cloudRect={cloudRect}
                onComplete={onTransitionComplete}
              // onReplay={resetTransition}
              />
            )}

      {/* Dev mode period switcher */}
      {/* <DevPeriodSwitcher
        currentPeriod={period}
        overridePeriod={overridePeriod}
        onOverrideChange={setOverridePeriod}
      /> */}
    </>
  );
}
